// Cloudflare Turnstile token helper (Task #3228).
//
// Lazily loads the Turnstile script, renders a single invisible / managed
// widget, and hands back a fresh single-use token on demand. The token is
// attached to the public, expensive requests the API server protects:
// signup / OTP request, address autocomplete, and quote estimation.
//
// The widget must only run in a production Vite build. Replit development and
// preview pages are nested under temporary hosts that are deliberately absent
// from Cloudflare's site-key allowlist; loading Turnstile there produces
// repeated 110200 errors and cannot yield a usable token. Keeping this guard
// beside the site-key lookup means every caller, including background warming,
// stays off outside production even if a key is accidentally injected.

const SITE_KEY = ((import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) ?? "").trim();
const IS_PRODUCTION_BUILD = import.meta.env.PROD === true;
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

// How long a single execute() may wait for the widget before we give up
// and resolve `null`. Kept short (Task #3502) so a foreground pricing
// interaction is never held hostage by the widget. A successful silent
// challenge resolves well within this; an interaction-only widget that
// can't produce one silently fails fast instead of stalling.
const TOKEN_WAIT_MS = 2500;

// A pre-warmed token TTL. Turnstile tokens are valid ~300s after issue;
// we hand out a warmed token only while comfortably inside that window.
const READY_TOKEN_TTL_MS = 240_000;

// Header name the API server reads (middleware/turnstile.ts TURNSTILE_HEADER).
export const TURNSTILE_HEADER = "cf-turnstile-response";

interface TurnstileApi {
  render(el: HTMLElement, opts: Record<string, unknown>): string;
  execute(id: string): void;
  reset(id: string): void;
  remove(id: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

export function isTurnstileConfigured(): boolean {
  return IS_PRODUCTION_BUILD && !!SITE_KEY;
}

let scriptPromise: Promise<void> | null = null;
function loadScript(): Promise<void> {
  if (typeof document === "undefined") return Promise.reject(new Error("no document"));
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => {
      // Allow a later retry to re-attempt the load.
      scriptPromise = null;
      reject(new Error("turnstile script load failed"));
    };
    document.head.appendChild(s);
  });
  return scriptPromise;
}

let container: HTMLElement | null = null;
let widgetId: string | null = null;
let pending: ((token: string | null) => void) | null = null;

function dispatch(token: string | null): void {
  if (pending) pending(token);
}

function ensureWidget(ts: TurnstileApi): void {
  if (widgetId != null) return;
  container = document.createElement("div");
  Object.assign(container.style, {
    position: "fixed",
    width: "0",
    height: "0",
    overflow: "hidden",
    bottom: "0",
    left: "0",
    zIndex: "-1",
  });
  document.body.appendChild(container);
  widgetId = ts.render(container, {
    sitekey: SITE_KEY,
    // Only fetch a token when we explicitly call execute(), and only show
    // UI if Cloudflare decides an interactive challenge is warranted — so
    // the normal (human) case stays invisible.
    execution: "execute",
    appearance: "interaction-only",
    callback: (token: string) => dispatch(token),
    "error-callback": () => dispatch(null),
    "timeout-callback": () => dispatch(null),
    "expired-callback": () => {
      /* token expired before use; the next request resets the widget */
    },
  });
}

async function requestToken(): Promise<string | null> {
  if (!isTurnstileConfigured()) return null;
  let ts: TurnstileApi | undefined;
  try {
    await loadScript();
    ts = window.turnstile;
  } catch {
    return null;
  }
  if (!ts) return null;

  try {
    ensureWidget(ts);
    if (widgetId == null) return null;
    // Clear any prior (single-use) token so execute() produces a fresh one.
    ts.reset(widgetId);
  } catch {
    return null;
  }

  return await new Promise<string | null>((resolve) => {
    let settled = false;
    const settle = (token: string | null) => {
      if (settled) return;
      settled = true;
      pending = null;
      resolve(token);
    };
    pending = settle;
    // Safety net: never hang a request waiting on the widget. The widget
    // is invisible / interaction-only, so when Cloudflare decides an
    // interactive challenge is needed no token can ever arrive silently —
    // resolve `null` quickly instead of stalling a foreground interaction.
    // The happy (human, silent) path resolves in a few hundred ms, well
    // under this cap. (Task #3502 — was 8000ms, which produced a ~9s
    // price-calc stall when serialized ahead of the quote request.)
    setTimeout(() => settle(null), TOKEN_WAIT_MS);
    try {
      ts!.execute(widgetId!);
    } catch {
      settle(null);
    }
  });
}

// A single widget can only have one execute() in flight at a time and its
// callback resolves the *current* pending request — so serialize callers.
let chain: Promise<unknown> = Promise.resolve();

/**
 * Resolve a fresh, single-use Turnstile token, or `null` when the feature
 * is not configured or the challenge could not be produced. Callers should
 * attach a non-null token via the `cf-turnstile-response` header / body
 * field and simply omit it when `null`.
 */
export function getTurnstileToken(): Promise<string | null> {
  const run = chain.then(() => requestToken()).catch(() => null);
  chain = run.catch(() => null);
  return run;
}

// ---------------------------------------------------------------------------
// Pre-warmed token (Task #3502)
//
// The price flow must never block on the widget. Instead it pre-warms a
// token in the background (off the critical path) and attaches it only if
// one is already in hand. A warmed token is single-use, so it is handed out
// at most once and a fresh one is warmed immediately after.
// ---------------------------------------------------------------------------

let readyToken: string | null = null;
let readyTokenAt = 0;
let warming = false;

/**
 * Kick off a background token fetch so a fresh token is waiting by the time
 * the user changes dates / options. No-op when the feature is unconfigured,
 * when a usable token is already cached, or when a warm is already running.
 * Never throws and never blocks the caller.
 */
export function prewarmTurnstileToken(): void {
  if (!isTurnstileConfigured()) return;
  if (warming) return;
  if (readyToken && Date.now() - readyTokenAt < READY_TOKEN_TTL_MS) return;
  warming = true;
  getTurnstileToken()
    .then((tok) => {
      if (tok) {
        readyToken = tok;
        readyTokenAt = Date.now();
      }
    })
    .catch(() => {
      /* swallow — a missing token simply means we attach none */
    })
    .finally(() => {
      warming = false;
    });
}

/**
 * Return a pre-warmed, not-yet-consumed token if one is ready right now,
 * otherwise `null` — never blocking on the widget. Consuming a token clears
 * it (single-use) and starts warming the next one. Use this on latency-
 * sensitive paths (pricing) where a missing token degrades gracefully via
 * the server's fail-open / `turnstile_required` 403 contract.
 */
export function takeReadyTurnstileToken(): string | null {
  const fresh = readyToken && Date.now() - readyTokenAt < READY_TOKEN_TTL_MS ? readyToken : null;
  readyToken = null;
  readyTokenAt = 0;
  // Prepare the next interaction's token without blocking this one.
  prewarmTurnstileToken();
  return fresh;
}
